import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { streamChat } from '../../services/chatStream';

export const fetchConversations = createAsyncThunk('chat/fetchConversations', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/chat/conversations');
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Impossible de charger les conversations.');
  }
});

export const fetchConversation = createAsyncThunk('chat/fetchConversation', async (conversationId, { rejectWithValue }) => {
  try {
    const response = await api.get(`/chat/conversations/${conversationId}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Impossible de charger la conversation.');
  }
});

export const sendMessage = createAsyncThunk('chat/sendMessage', async ({ conversationId, message, voice = false }, { dispatch, requestId, rejectWithValue }) => {
  try {
    return await streamChat({
      conversationId,
      message,
      voice,
      onDelta: (text) => dispatch(chatSlice.actions.streamDelta({ requestId, text })),
      onAction: (action) => dispatch(chatSlice.actions.streamAction({ requestId, action })),
    });
  } catch (error) {
    return rejectWithValue(error.message || 'Échec de l’envoi du message.');
  }
});

// Records what became of an action Gladys proposed (done | dismissed |
// failed); the action itself was executed by the caller.
export const settleChatAction = createAsyncThunk('chat/settleChatAction', async ({ messageId, actionId, status }, { rejectWithValue }) => {
  try {
    const response = await api.patch(`/chat/messages/${messageId}/actions/${actionId}`, { status });
    return { messageId, action: response.data };
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Impossible d’enregistrer le résultat de l’action.');
  }
});

export const deleteConversation = createAsyncThunk('chat/deleteConversation', async (conversationId, { rejectWithValue }) => {
  try {
    await api.delete(`/chat/conversations/${conversationId}`);
    return conversationId;
  } catch (error) {
    return rejectWithValue(error.response?.data?.error || 'Impossible de supprimer la conversation.');
  }
});

const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    conversations: [],
    currentConversation: null,
    loading: false,
    sending: false,
    error: null,
  },
  reducers: {
    clearChatError: (state) => {
      state.error = null;
    },
    setCurrentConversation: (state, action) => {
      state.currentConversation = action.payload;
    },
    clearCurrentConversation: (state) => {
      state.currentConversation = null;
    },
    // Appends a streamed reply fragment, only while the request's own pending
    // user message is still on screen (ignored after switching conversation).
    streamDelta: (state, action) => {
      const { requestId, text } = action.payload;
      const msgs = state.currentConversation?.messages;
      if (!msgs || !msgs.some((m) => m.id === `pending-${requestId}`)) return;
      const id = `streaming-${requestId}`;
      const reply = msgs.find((m) => m.id === id);
      if (reply) reply.content += text;
      else msgs.push({ id, role: 'assistant', content: text, created_at: new Date().toISOString(), streaming: true });
    },
    // A proposed action arrives while the reply streams (confirmable once saved).
    streamAction: (state, action) => {
      const { requestId, action: proposal } = action.payload;
      const msgs = state.currentConversation?.messages;
      if (!msgs || !msgs.some((m) => m.id === `pending-${requestId}`)) return;
      const id = `streaming-${requestId}`;
      let reply = msgs.find((m) => m.id === id);
      if (!reply) {
        reply = { id, role: 'assistant', content: '', created_at: new Date().toISOString(), streaming: true };
        msgs.push(reply);
      }
      reply.actions = [...(reply.actions || []), proposal];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchConversation.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.currentConversation = action.payload;
      })
      .addCase(fetchConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(sendMessage.pending, (state, action) => {
        state.sending = true;
        state.error = null;
        const { message } = action.meta.arg;
        if (!state.currentConversation) {
          state.currentConversation = { id: 0, title: message.slice(0, 60), messages: [] };
        }
        state.currentConversation.messages = [
          ...(state.currentConversation.messages || []),
          { id: `pending-${action.meta.requestId}`, role: 'user', content: message, created_at: new Date().toISOString(), pending: true },
        ];
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sending = false;
        state.currentConversation = action.payload.conversation;
        // Update or add conversation to list
        const idx = state.conversations.findIndex(c => c.id === action.payload.conversation.id);
        if (idx >= 0) {
          state.conversations[idx] = { ...action.payload.conversation, messages: undefined };
        } else {
          state.conversations.unshift({ ...action.payload.conversation, messages: undefined });
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sending = false;
        state.error = action.payload;
        const conv = state.currentConversation;
        if (conv) {
          const ids = [`pending-${action.meta.requestId}`, `streaming-${action.meta.requestId}`];
          conv.messages = (conv.messages || []).filter((m) => !ids.includes(m.id));
          if (!conv.id && conv.messages.length === 0) state.currentConversation = null;
        }
      })
      .addCase(settleChatAction.fulfilled, (state, action) => {
        const { messageId, action: settled } = action.payload;
        const msg = state.currentConversation?.messages?.find((m) => m.id === messageId);
        const target = msg?.actions?.find((a) => a.id === settled?.id);
        if (target) target.status = settled.status;
      })
      .addCase(deleteConversation.fulfilled, (state, action) => {
        state.conversations = state.conversations.filter(c => c.id !== action.payload);
        if (state.currentConversation?.id === action.payload) {
          state.currentConversation = null;
        }
      });
  },
});

export const { clearChatError, setCurrentConversation, clearCurrentConversation, streamDelta, streamAction } = chatSlice.actions;
export default chatSlice.reducer;
